import { useState, type ChangeEvent } from "react";
import {
  ClientFileValidationError,
  UPLOAD_ERROR_MESSAGES,
  uploadImage,
  type FileEntityType,
  type UploadedImage,
} from "./fileUpload";
import { resolveErrorMessage, type ResolvedError } from "./errorMessage";
import { useToast } from "./PortalPrimitives";

// PLAN-007 §7 (fase 3), admin.jsx:145: `<label className="chip">` envolviendo
// un `<input type="file" hidden>` — el patrón exacto del prototipo, no un
// `<button>` con `ref.click()`. Compartido por las 7 pantallas que
// reemplazan el `<input placeholder="https://…">`: cada una solo cambia
// `entityType`, la etiqueta visible y qué hace con el `fileId`/`imageUrl`
// resultante (`onUploaded`).
export interface FileUploadChipProps {
  readonly entityType: FileEntityType;
  readonly label: string;
  readonly ariaLabel: string;
  readonly disabled?: boolean;
  readonly onUploaded: (image: UploadedImage) => void;
}

export function FileUploadChip({
  entityType,
  label,
  ariaLabel,
  disabled,
  onUploaded,
}: FileUploadChipProps) {
  const toast = useToast();
  const [uploading, setUploading] = useState(false);

  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Limpia el input ya: sin esto, elegir el mismo archivo dos veces
    // seguidas (p. ej. tras un error) no dispara `onChange` la segunda vez.
    event.target.value = "";
    if (!file) return;

    setUploading(true);
    try {
      const uploaded = await uploadImage(file, entityType);
      onUploaded(uploaded);
    } catch (error) {
      // brief §4: el rechazo de tamaño en cliente (antes de pedir la URL) no
      // es una `ApiError` — `resolveErrorMessage` solo sabe traducir esa
      // clase, así que el mensaje ya resuelto de
      // `ClientFileValidationError` se usa directo.
      const resolved: ResolvedError =
        error instanceof ClientFileValidationError
          ? { message: error.message }
          : resolveErrorMessage(error, UPLOAD_ERROR_MESSAGES);
      toast.show(resolved.message, "error", {
        correlationId: resolved.correlationId,
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <label
      className="chip"
      style={{
        flex: "none",
        opacity: disabled || uploading ? 0.6 : 1,
        cursor: disabled || uploading ? "default" : "pointer",
      }}
    >
      {uploading ? "Subiendo…" : label}
      <input
        type="file"
        accept="image/*"
        aria-label={ariaLabel}
        style={{ display: "none" }}
        disabled={disabled || uploading}
        onChange={(event) => {
          void handleChange(event);
        }}
      />
    </label>
  );
}
